import React, { useEffect, useMemo, useState } from 'react'
import { Alert } from 'antd'
import FulcrumLoadProgress from './FulcrumLoadProgress'
import {
  fetchFulcrumDatasets,
  getCachedFulcrumDatasets,
  subscribeFulcrumRefresh,
} from '../../utilities/fulcrumData'

const buildInitialProgress = (datasets) =>
  datasets.reduce((acc, key) => {
    acc[key] = 'pending'
    return acc
  }, {})

const FulcrumRoute = ({ datasets, children }) => {
  const uniqueDatasets = useMemo(() => [...new Set(datasets)], [datasets.join(',')])
  const [data, setData] = useState(() => getCachedFulcrumDatasets(uniqueDatasets))
  const [error, setError] = useState(null)
  const [progress, setProgress] = useState(() => buildInitialProgress(uniqueDatasets))
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    return subscribeFulcrumRefresh(() => {
      setRefreshToken((token) => token + 1)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    const cached = getCachedFulcrumDatasets(uniqueDatasets)

    if (cached) {
      setData(cached)
      setError(null)
      setProgress(
        uniqueDatasets.reduce((acc, key) => {
          acc[key] = 'done'
          return acc
        }, {})
      )
      return undefined
    }

    setData(null)
    setError(null)
    setProgress(buildInitialProgress(uniqueDatasets))

    fetchFulcrumDatasets(uniqueDatasets, (status) => {
      if (!cancelled) {
        setProgress(status)
      }
    })
      .then((loaded) => {
        if (!cancelled) {
          setData(loaded)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err)
        }
      })

    return () => {
      cancelled = true
    }
  }, [uniqueDatasets.join(','), refreshToken])

  if (error) {
    return (
      <div className="fulcrum-route">
        <Alert
          type="error"
          showIcon
          message="Could not load data"
          description={error.message || 'Please try again.'}
        />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="fulcrum-route">
        <FulcrumLoadProgress overlay datasets={uniqueDatasets} progress={progress} />
      </div>
    )
  }

  return <div className="fulcrum-route">{children(data)}</div>
}

export default FulcrumRoute
